import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { m, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { usePlatformMode } from "@/hooks/usePlatformMode";
import { useFeatureToggles } from "@/hooks/useFeatureToggle";
import { HeadphonesIcon, Wrench, Package, Store as StoreIcon } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useTheme } from "@/contexts/ThemeContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import cpLogoLight from "@/assets/cp-logo-light.png";
import cpLogoDark from "@/assets/cp-logo-dark.png";
import { ShoppingCart, User, Menu, X, LogOut, LayoutDashboard, Sun, Moon, MessageCircle } from "lucide-react";
import { NotificationBell } from "@/components/NotificationBell";

export const Header = () => {
  const { t } = useTranslation();
  const { user, isAdmin, staffRole, signOut } = useAuth();
  const { enabled: platformMode } = usePlatformMode();
  const { toggles } = useFeatureToggles();
  const { itemCount } = useCart();
  const { theme, toggleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isStoreOwner, setIsStoreOwner] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      supabase.from("user_roles").select("role").eq("user_id", user.id).eq("role", "store_owner").then(({ data }) => {
        setIsStoreOwner((data || []).length > 0);
      });
    } else {
      setIsStoreOwner(false);
    }
  }, [user]);

  // Desktop nav: only essential links
  const desktopNavLinks = [
    { label: t("nav.home"), to: "/" },
    { label: t("nav.shop"), to: "/shop" },
    { label: t("nav.ecosystem"), to: "/ecosystem" },
    ...(toggles.marketplace ? [{ label: t("nav.marketplace"), to: "/marketplace" }] : []),
    { label: t("nav.passport"), to: "/verify" },
    ...(toggles.repairs ? [{ label: t("nav.repair"), to: "/repair" }] : []),
    ...(platformMode ? [{ label: t("nav.partners"), to: "/partners" }] : []),
  ];

  // Mobile nav: all links
  const mobileNavLinks = [
    { label: t("nav.home"), to: "/" },
    { label: t("nav.shop"), to: "/shop" },
    { label: t("nav.ecosystem"), to: "/ecosystem" },
    ...(toggles.compare ? [{ label: t("nav.compare"), to: "/compare" }] : []),
    ...(toggles.marketplace ? [{ label: t("nav.marketplace"), to: "/marketplace" }] : []),
    ...(toggles.sell_phone ? [{ label: t("nav.sell"), to: "/sell" }] : []),
    ...(toggles.repairs ? [{ label: t("nav.repair"), to: "/repair" }] : []),
    ...(toggles.phone_recommendation ? [{ label: t("nav.recommend"), to: "/recommend" }] : []),
    { label: t("nav.passport"), to: "/verify" },
    { label: t("nav.giveaways"), to: "/giveaways" },
    ...(platformMode ? [
      { label: t("nav.partners"), to: "/partners" },
    ] : []),
  ];

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <m.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="sticky top-0 z-50 w-full max-w-full overflow-x-clip bg-card/80 backdrop-blur-xl shadow-md border-b border-primary/10"
    >
      <div className="max-w-7xl mx-auto flex w-full max-w-full min-w-0 items-center justify-between gap-2 px-2.5 py-3 sm:px-4">
        <Link to="/" className="flex items-center shrink-0 hover:opacity-90 transition-opacity">
          <m.img 
            src={theme === "dark" ? cpLogoDark : cpLogoLight} 
            alt="Chawni" 
            width={56}
            height={56}
            className={`w-12 h-12 sm:w-14 sm:h-14 object-contain ${theme === "dark" ? "mix-blend-screen" : "mix-blend-multiply"}`} 
            fetchPriority="high"
            decoding="async"
            whileHover={{ y: -2, scale: 1.05 }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
          />
        </Link>

        <nav className="hidden md:flex items-center gap-4 lg:gap-6 min-w-0 flex-1 justify-center">
          {desktopNavLinks.map((link) => (
            <Link key={link.to} to={link.to} className="text-sm font-medium text-foreground hover:text-primary transition-colors whitespace-nowrap">
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="flex min-w-0 shrink-0 items-center gap-0 sm:gap-1 max-w-full">
          <div className="hidden md:block">
            <LanguageSwitcher />
          </div>
          <Button variant="ghost" size="icon" onClick={toggleTheme} className="hidden h-9 w-9 md:inline-flex" aria-label={t("common.toggleTheme")}>
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
          <Button variant="ghost" size="icon" className="relative h-9 w-9" asChild aria-label={t("common.cart")}>
            <Link to="/cart">
              <ShoppingCart className="w-5 h-5" />
              {itemCount > 0 && (
                <m.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-primary-foreground text-[10px] rounded-full flex items-center justify-center font-bold">
                  {itemCount}
                </m.span>
              )}
            </Link>
          </Button>
          <NotificationBell />
          {user ? (
            <div className="hidden md:flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-9 w-9" asChild aria-label={t("common.messages")}>
                <Link to="/messages"><MessageCircle className="w-5 h-5" /></Link>
              </Button>
              {isAdmin && (
                <Button variant="ghost" size="icon" className="h-9 w-9" asChild aria-label={t("common.admin")}>
                  <Link to="/admin"><LayoutDashboard className="w-5 h-5" /></Link>
                </Button>
              )}
              {!!staffRole && (
                <Button variant="ghost" size="icon" className="h-9 w-9 relative" asChild aria-label={t("common.staff")}>
                  <Link to="/staff">
                    {staffRole === "support_agent" ? <HeadphonesIcon className="w-5 h-5" /> :
                     staffRole === "repair_manager" ? <Wrench className="w-5 h-5" /> :
                     <Package className="w-5 h-5" />}
                  </Link>
                </Button>
              )}
              {isStoreOwner && (
                <Button variant="ghost" size="icon" className="h-9 w-9" asChild aria-label={t("common.partner")}>
                  <Link to="/partner/dashboard"><StoreIcon className="w-5 h-5" /></Link>
                </Button>
              )}
              <Button variant="ghost" size="icon" className="h-9 w-9" asChild aria-label={t("common.profile")}>
                <Link to="/dashboard"><User className="w-5 h-5" /></Link>
              </Button>
              <Button variant="ghost" size="icon" className="h-9 w-9" onClick={handleSignOut} aria-label={t("common.signOut")}>
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          ) : (
            <Button size="sm" className="hidden md:flex" asChild>
              <Link to="/auth">{t("nav.signIn")}</Link>
            </Button>
          )}
          <Button variant="ghost" size="icon" className="md:hidden h-9 w-9 shrink-0" onClick={() => setMobileOpen(!mobileOpen)} aria-label={mobileOpen ? t("common.closeMenu") : t("common.openMenu")}>
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <m.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="md:hidden max-w-full overflow-x-hidden overflow-y-hidden border-t border-primary/10 bg-card">
            <div className="space-y-2 px-4 py-4">
              <div className="flex items-center gap-2 pb-2">
                <LanguageSwitcher />
                <Button variant="outline" size="sm" className="h-9 rounded-lg" onClick={toggleTheme}>
                  {theme === "dark" ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
                  {theme === "dark" ? t("nav.lightMode") : t("nav.darkMode")}
                </Button>
              </div>
              {mobileNavLinks.map((link) => (
                <Link key={link.to} to={link.to} className="block py-2 text-foreground hover:text-primary font-medium" onClick={() => setMobileOpen(false)}>
                  {link.label}
                </Link>
              ))}
              {user ? (
                <>
                  <Link to="/messages" className="block py-2 text-foreground hover:text-primary font-medium" onClick={() => setMobileOpen(false)}>{t("nav.messages")}</Link>
                  <Link to="/dashboard" className="block py-2 text-foreground hover:text-primary font-medium" onClick={() => setMobileOpen(false)}>{t("nav.myAccount")}</Link>
                  {isAdmin && <Link to="/admin" className="block py-2 text-foreground hover:text-primary font-medium" onClick={() => setMobileOpen(false)}>{t("nav.adminDashboard")}</Link>}
                  {!!staffRole && <Link to="/staff" className="block py-2 text-foreground hover:text-primary font-medium" onClick={() => setMobileOpen(false)}>📋 {t("nav.staffDashboard")}</Link>}
                  {isStoreOwner && <Link to="/partner/dashboard" className="block py-2 text-foreground hover:text-primary font-medium" onClick={() => setMobileOpen(false)}>🏪 {t("nav.partnerDashboard")}</Link>}
                  <button onClick={() => { handleSignOut(); setMobileOpen(false); }} className="block py-2 text-destructive font-medium">{t("nav.signOut")}</button>
                </>
              ) : (
                <Link to="/auth" className="block py-2 text-primary font-bold" onClick={() => setMobileOpen(false)}>{t("nav.signInRegister")}</Link>
              )}
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </m.header>
  );
};
